import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { ConfirmButtonColors, ConfirmationModal } from '../../../3-pattern';
import { Box } from '../../../1-primative';
import { TranslatableTextField } from '../../../2-component';
import { TRIGGER_MODEL_PORTFOLIO_RESIGN } from '../../../../pages/buildModels/components/resignIPS';
import { TranslatedString } from '../../../../interfaces/shared';
import { useGlobalToast } from '../../../../providers/globalToastProvider';

interface TriggerResignIpsDialogProps {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  modelId: string;
}

export const TriggerResignIpsDialog = ({
  open,
  onClose,
  modelId,
  loading = false,
}: TriggerResignIpsDialogProps) => {
  const { t } = useTranslation(['modelPortfolios', 'shared', 'buildModels']);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [translatedReason, setTranslatedReason] = useState<TranslatedString>({ en: '', fr: '' });
  const { showToast } = useGlobalToast();

  const [triggerModelResign, { loading: triggerLoading }] = useMutation(TRIGGER_MODEL_PORTFOLIO_RESIGN, {
    variables: {
      input: {
        modelPortfolioId: modelId,
        translatedReasonForUpdate: translatedReason,
      },
    },
    onCompleted: () => {
      showToast({
        message: t('buildModels:edit.triggerReSign.toastMessage'),
        severity: 'info',
      });
    },
  });

  useEffect(() => {
    if (!open) {
      setTranslatedReason({ en: '', fr: '' });
      setShowConfirmation(false);
    }
  }, [open]);

  const handleProceed = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    await triggerModelResign();
    onClose();
    setTranslatedReason({ en: '', fr: '' });
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    onClose();
    setTranslatedReason({ en: '', fr: '' });
    setShowConfirmation(false);
  };

  return (
    <>
      <ConfirmationModal
        open={open && !showConfirmation}
        title={t('buildModels:edit.triggerReSign.button')}
        onConfirm={handleProceed}
        onCancel={handleCancel}
        confirmButtonLabel={t('buildModels:edit.triggerReSign.triggerButton')}
        cancelButtonLabel={t('buildModels:transitionTheme.archiveConfirmationModal.cancel')}
        confirmButtonColor={ConfirmButtonColors.primary}
        loading={loading}
        confirmButtonDisabled={!translatedReason.en && !translatedReason.fr}
        dialogStyles={{
          dialogContainer: {
            '& .MuiDialog-container': {
              '& .MuiPaper-root': {
                width: '100%',
                maxWidth: '416px',
              },
            },
          },
          dialogContentStyles: {
            width: 'auto',
            maxWidth: '421px',
            minWidth: 'auto',
          },
        }}
      >
        <Box>
          <TranslatableTextField
            testId="model-portfolio-resign-ips-dialog-reason-field"
            label={t('buildModels:edit.triggerReSign.updateReason')}
            fullWidth
            value={translatedReason}
            onChange={(e: any) => setTranslatedReason(e)}
            multiline
          />
        </Box>
      </ConfirmationModal>

      <ConfirmationModal
        open={open && showConfirmation}
        title={t('buildModels:edit.triggerReSign.confirmationTitle')}
        bodyText={t('buildModels:edit.triggerReSign.confirmationMessage')}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmButtonLabel={t('shared:proceed')}
        cancelButtonLabel={t('buildModels:transitionTheme.archiveConfirmationModal.cancel')}
        confirmButtonColor={ConfirmButtonColors.primary}
        loading={loading || triggerLoading}
        dialogStyles={{
          dialogContainer: {
            '& .MuiDialog-container': {
              '& .MuiPaper-root': {
                width: '100%',
                maxWidth: '416px',
              },
            },
          },
        }}
      />
    </>
  );
};
